import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { get, isEqual, isNil, isUndefined } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  authenticationLink,
  authenticationLoginEmail,
  authenticationLoginPassword,
  logout,
  updateUser,
} from '@/store/slices/authentication';
import { sessionEnsure, updateSessionPostAuth } from '@/store/slices/session';
import { isEmailValid } from '@/utils/strings';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveLoginRedirect } from '@/utils/login-redirect';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';
import PageLoader from '@/components/page-loader';

import { ROUTE } from '../../routes';
import { Text } from '../../components/Text';
import { Divider } from '../../components/Divider';
import { useLanguageSwitcher } from '../../hooks/useLanguageSwitcher';
import { SocialLoginBar } from '../social-login/components/SocialLoginBar';
import { AuthLogo } from './components/AuthLogo';
import { CtaHint, CTA_HINT_ID } from './components/CtaHint';
import { LanguagePicker } from './components/LanguagePicker';
import { useAuthCardMorph } from './useAuthCardMorph';
import { useAuthInputFocus } from './useAuthInputFocus';
import { initialAuthMode, authModeOrder } from './authMode';
import { Login } from './login.styles';

const MAIL_SENT_SRC = 'https://micros-t.b-cdn.net/owners-hub-register-mail-sent-compact-1777293313810.svg';

type LoginLayout = 'form' | 'next';

// Which sign-in method the shared form is showing. This build only ever runs as web, so the
// default is `magic` (listed first, via ./authMode) and the toggle lets the user switch to
// `password`. The authMode helper takes a `native` flag so a separate native build can default to
// `password` instead; on web that flag is always false.
type LoginMode = 'magic' | 'password';

/**
 * `/login`: magic-link sign-in.
 *
 * Hand-written, strictly-typed reproduction of the config-driven
 * `module.owners.hub.login` (`src/components/modules/login/index.tsx`): the auth
 * flow is identical, request a magic link by email, swap to the "email sent"
 * view, and complete sign-in when the user returns via the `?link=` param, then
 * route on with {@link resolvePostAuthRoute}. The visual tree mirrors the
 * template node-for-node (styles in `login.styles.ts`). Rendered full-bleed
 * OUTSIDE the account chrome (see `pages/login/router.tsx`).
 */
const PageLogin = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  const { innerRef, cardStyle } = useAuthCardMorph();

  const authentication = useAppSelector((state) => state.authentication, isEqual);
  const casinoInstance = useAppSelector((state) => state.casinoInstance, isEqual);
  const session = useAppSelector((state) => state.session, isEqual);

  const [searchParams, setSearchParams] = useSearchParams();
  const [layout, setLayout] = React.useState<LoginLayout>('form');
  const [email, setEmail] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState<boolean>(false);
  // True once THIS magic link's sign-in has fully resolved: the auth token is set AND its
  // session + brands have loaded (authenticationLink awaits both before fulfilling). Routing
  // waits on this rather than matching the link's session_id to the loaded session, because
  // the backend resolves /session/load to the owner's canonical in-progress session, whose id
  // differs from the link's session_id. An id-equality gate could therefore never be satisfied
  // for a returning owner, and the page hung on the loader forever (incognito only worked
  // because a brand-new owner has no canonical session, so the ids happened to coincide).
  const [linkResolved, setLinkResolved] = React.useState<boolean>(false);
  // True once THIS magic link's OWN redemption has failed (the link itself, or one of its
  // awaited follow-up fetches). Tracked separately from the shared `authentication.error`
  // because a stale boot stale-token check rejecting on the OLD localStorage token ALSO sets
  // `authentication.error` while this (impersonation) redemption is still succeeding — and we
  // must NOT treat that as a link failure (it would tear `?link=` out of the URL, see below).
  const [linkFailed, setLinkFailed] = React.useState<boolean>(false);
  // True when THIS magic link failed specifically because it had already lapsed
  // (API `LOGIN_LINK_EXPIRED`). Drives the red "your login link has expired" notice
  // above the email field, so a returning owner who clicked a stale link lands on the
  // sign-in form with a clear reason + next step instead of a bare, unexplained form.
  const [linkExpired, setLinkExpired] = React.useState<boolean>(false);
  // This build is never native, so the form defaults to `magic` (the password-free primary web
  // flow) and lists it first. The flag is passed through the shared authMode helper (which a
  // native build can drive to `password`); here it is always false. The toggle lists the default first.
  const nativeApp = false;
  const [mode, setMode] = React.useState<LoginMode>(() => initialAuthMode(nativeApp));
  // Password-mode field + its show/hide affordance. The value is shared with neither the
  // email field nor the magic-link flow.
  const [password, setPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  // True once a password sign-in has failed: drives the single "email or password is
  // incorrect" notice (D4 keeps every failure to one message). Cleared when the owner edits
  // nothing but switches mode, and reset before each fresh submit.
  const [passwordError, setPasswordError] = React.useState<boolean>(false);
  // True once THIS password sign-in has fully resolved (token set, session + brands loaded,
  // the thunk awaits both). The routing effect below waits on it, exactly like `linkResolved`
  // does for the magic-link flow.
  const [passwordResolved, setPasswordResolved] = React.useState<boolean>(false);
  // True once a Google SSO sign-in has fully resolved (token + brands + session settled, the thunk
  // awaits all three). Opens the routing gate below, exactly like `passwordResolved`. Telegram does
  // NOT set this: it is a full-page redirect finished on the /telegram-callback page.
  const [ssoResolved, setSsoResolved] = React.useState<boolean>(false);
  // Autofocuses the email field on show but suppresses the browser autofill popup
  // on that programmatic focus (see useAuthInputFocus); spread inputProps on the input.
  const { emailRef, inputProps } = useAuthInputFocus(layout === 'form' && !loading);
  // Where the auth service aimed THIS link (`redirect_url` on the redemption response),
  // already resolved to an in-app path. Held in a ref rather than state because it is
  // written in the same tick as `setLinkResolved`, so the routing effect below reads it on
  // the very render that opens its gate, and because it must not be able to re-trigger that
  // effect itself: a second run would recompute the default route and navigate away from
  // the destination we were just told to honour.
  const redirectTargetRef = React.useRef<string | null>(null);

  // Magic link from the URL (?link=…), set when the user clicks the emailed link.
  const magicLink = React.useMemo<string | null>(() => searchParams.get('link'), [searchParams]);

  // Magic link request. `authenticationLink` awaits its own userBrandsList + sessionLoad, so
  // when its promise resolves the auth token is set and the session + brands are loaded: mark
  // the link flow resolved and let routing proceed. A failure (the link itself, or either
  // follow-up fetch) flips `linkFailed`, which the error effect below reacts to by clearing the
  // spinner + dead link — so a transient brands/session error no longer hangs the page on the loader.
  React.useEffect(() => {
    if (!isNil(magicLink)) {
      setLoading(true);
      dispatch(authenticationLink({ link: magicLink }))
        .unwrap()
        .then((result) => {
          // The auth service decides where a redeemed link lands; keep it for the routing
          // effect below. An absent/unusable value resolves to null and routing falls back
          // to the brand-state default.
          redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
          setLinkResolved(true);
        })
        .catch((rejected: unknown) => {
          // Record that THIS redemption failed so the error effect below can clear the dead
          // link — without keying off the shared `authentication.error`, which a concurrent
          // stale boot check on the OLD token also sets while this redemption is succeeding.
          setLinkFailed(true);
          // A lapsed link resolves to the API code `LOGIN_LINK_EXPIRED` (plumbed onto the
          // thunk's reject payload); surface the targeted expired notice for that case.
          if (get(rejected, 'code') === 'LOGIN_LINK_EXPIRED') {
            setLinkExpired(true);
          }
        });
    }
  }, [dispatch, magicLink]);

  // Magic link success → route on, but ONLY once the link flow has resolved AND the session it
  // loaded has settled. We deliberately do NOT require session.sessionId to equal the link's
  // session_id: /session/load resolves to the owner's canonical session (a different id than
  // the link reports), so an equality gate never holds for a returning owner and the page would
  // hang on the loader. On this route nothing else loads the session (Effect 4 is gated out
  // while ?link= is present, and session starts unloaded), so session.loaded becoming true can
  // only be the link's own load: there is no stale pre-existing session to misroute on.
  //
  // The gate is `linkResolved` (set once on a SUCCESSFUL redemption, never reset), NOT the
  // live presence of `?link=` in the URL. A stale boot stale-token check rejecting on the OLD
  // localStorage token sets `authentication.error`, which the error effect below reacts to by
  // deleting `?link=` — so an impersonation sign-in over an already-active session would have
  // its `?link=` cleared mid-flight, and a `!isNil(magicLink)` gate here would then NEVER fire,
  // stranding the (already authenticated) admin on the auth screen. Keying on `linkResolved`
  // makes routing survive that deletion.
  React.useEffect(() => {
    const authCompleted = !authentication.in_progress && authentication.auth_type === 'user';
    const casinoCompleted = !casinoInstance.loading && casinoInstance.loaded;
    // Either sign-in flow opens this gate: the magic-link redemption (`linkResolved`) or the
    // password sign-in (`passwordResolved`). Both set their flag only after the token is set
    // AND the session + brands have loaded, so the same session-settled check applies to both.
    const authenticatedSessionReady =
      (linkResolved || passwordResolved || ssoResolved) && session.loaded && !session.loading;

    if (authCompleted && casinoCompleted && authenticatedSessionReady) {
      setLoading(true);
      // A `redirect_url` on the redemption response WINS over the brand-state routing,
      // including its mid-setup onboarding branch: the link was minted for a destination
      // (support impersonation aimed at a page, a mail pointing at one) and that intent
      // outranks what we would have guessed from the session.
      navigate(
        redirectTargetRef.current ??
          resolvePostAuthRoute({
            sessionStatus: session.status,
            brandsLoaded: casinoInstance.loaded,
            brandsCount: casinoInstance.brands.length,
            configuratorBase: resolveConfiguratorFlowBase(),
          }),
        // Replace a `?link=` entry: left in history, Back would put the credential URL in front
        // of the already-loaded tag manager.
        { replace: magicLink !== null },
      );
    }
  }, [authentication, casinoInstance, session, navigate, linkResolved, passwordResolved, ssoResolved, magicLink]);

  // Magic link error → drop the spinner and clear the dead link from the URL. Gated on
  // `linkFailed` (THIS redemption rejected), NOT the shared `authentication.error`: a stale boot
  // stale-token check on the OLD localStorage token sets `authentication.error` even while an
  // impersonation redemption is succeeding, and reacting to that would wrongly delete `?link=`.
  React.useEffect(() => {
    if (linkFailed && !authentication.in_progress) {
      setLoading(false);
      searchParams.delete('link');
      setSearchParams(searchParams, { replace: true });
    }
  }, [linkFailed, authentication, searchParams, setSearchParams]);

  // Post-authentication handler: "mail" → show the email-sent view; "redirect" → route on.
  React.useEffect(() => {
    const nextPage = resolvePostAuthRoute({
      sessionStatus: session.status,
      brandsLoaded: casinoInstance.loaded,
      brandsCount: casinoInstance.brands.length,
      configuratorBase: resolveConfiguratorFlowBase(),
    });

    switch (session.postAuth) {
      case 'mail':
        setLayout('next');
        break;
      case 'redirect':
        setLoading(true);
        navigate(nextPage);
        break;
      default:
        break;
    }
  }, [
    navigate,
    session.postAuth,
    session.status,
    casinoInstance.brands.length,
    casinoInstance.loaded,
    casinoInstance.loading,
  ]);

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setEmail(event.target.value);
  }, []);

  const isValid = React.useMemo<boolean>(() => isEmailValid(email), [email]);

  // Guards a double-click from racing two mints while the first submit is still ensuring
  // a session (that request is async now that minting is deferred to submit).
  const submittingRef = React.useRef(false);

  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || !isValid || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      // The owner is acting on the recovery (requesting a fresh link), so retire the
      // stale "expired" notice — it referred to the dead link they just left behind.
      setLinkExpired(false);

      // This page is NOT auth-gated, so an already-signed-in owner can reach it (browser back, or
      // typing the URL) and submit a DIFFERENT email. That is the case the sign-out below exists
      // for, and the reason it is unconditional.
      //
      // Sign the CURRENT identity out before the mint below, or the session is created owned by
      // the PREVIOUS owner (the mint sends no token of its own, so `sessionLoad` falls back to
      // whatever is in the store, and the backend takes ownership from that header) and the
      // emailed link then fails with an owner mismatch on redemption. Scoped so the marketing
      // hand-off and the language pick survive: both are read moments later, by the mint and by
      // the magic-link email. Full rationale and its accepted trade-offs live in
      // `store/visitor-context-teardown.ts`. Must precede `updateUser`, which logout would clear.
      dispatch(logout({ keepVisitorContext: true }));

      dispatch(updateUser({ email }));

      // Mint-then-send, sequentially. The magic-link request REQUIRES a session_id to bind the
      // sign-in to, so establish a session FIRST, then request the link with the resulting id.
      // `fresh: true` MINTS for the typed email and does NOT resume whatever session id is left in
      // this browser's localStorage: that id may be a previous user's or an already-advanced build,
      // and resuming it loaded the wrong session and killed the flow. The owner's real in-progress
      // build is reconciled by the backend by email on magic-link return. Minting is deferred to
      // here (was on mount) so bounced visits no longer leave email-less rows. The `?link=` return
      // flow above is orthogonal: it loads its own session and never reaches this handler.
      void dispatch(sessionEnsure({ email, fresh: true }))
        .unwrap()
        .then((result) => {
          if (!isNil(result.id)) {
            dispatch(authenticationLoginEmail({ email, sessionId: result.id }));
          }
        })
        .catch(() => {
          // Ensure/mint failed (e.g. network). Leave the form interactive so the user can retry.
        })
        .finally(() => {
          submittingRef.current = false;
        });
    },
    [dispatch, email, isValid],
  );

  const handlePasswordChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPassword(event.target.value);
  }, []);

  // Switch sign-in method. Clears the failure notice (it referred to the method left behind)
  // and deliberately leaves the shared email value untouched.
  const handleModeChange = React.useCallback((next: LoginMode) => {
    setMode(next);
    setPasswordError(false);
  }, []);

  // Password sign-in enable gate. The handler self-gates on the same predicate (react rule:
  // guards live in handlers, not only in styling).
  const canSubmitPassword = isValid && password.length > 0;

  // Password sign-in. Unlike the magic-link handler there is NO logout / sessionEnsure /
  // updateUser: `/login` is redemption-equivalent (D3), the thunk itself sets the token and
  // awaits the session + brands load, so we only mark the flow resolved and let the routing
  // effect above route on with any `redirect_url` the response carried.
  const handlePasswordSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || !isValid || password.length === 0 || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setPasswordError(false);
      setLoading(true);

      dispatch(authenticationLoginPassword({ email, password }))
        .unwrap()
        .then((result) => {
          // The auth service decides where a password sign-in lands, same as a redeemed link;
          // keep it for the routing effect above (absent/unusable resolves to null → default).
          redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
          setPasswordResolved(true);
        })
        .catch((rejected: unknown) => {
          setLoading(false);
          // D4: every sign-in failure surfaces the SAME single notice, so the error is set
          // unconditionally. The reject payload's `code` (`INVALID_CREDENTIALS` for a wrong
          // email/password) is intentionally NOT branched on, so we never reveal which field
          // was wrong; `get(rejected, 'code')` stays available if D4 is ever revisited.
          setPasswordError(true);
        })
        .finally(() => {
          submittingRef.current = false;
        });
    },
    [dispatch, email, isValid, password],
  );

  // Back from the "email sent" view to the sign-in form. postAuth is a one-shot
  // 'mail' signal that drove us here; clear it so the post-auth effect doesn't
  // immediately snap the layout back to 'next' on the next session update.
  const handleBackToForm = React.useCallback(() => {
    dispatch(updateSessionPostAuth(null));
    setLayout('form');
  }, [dispatch]);

  if (loading) {
    return <PageLoader />;
  }

  // Root class is `nova-login`, NOT `login`: the config-driven login module's global
  // stylesheet is scoped `.login .page .main .body { … }` and our DOM mirrors that
  // `.page > .main > .body` structure, so reusing `.login` here let those
  // higher-specificity rules (height/padding/justify-content) win over our own and
  // leak in. A non-colliding root class keeps this page fully self-styled.
  return (
    <Login className="nova-login">
      <div className="page">
        <div className="main">
          <div className="header">
            <AuthLogo />
          </div>

          <div className="body" style={cardStyle}>
            <div className="body-inner" ref={innerRef}>
              <div className="layout">
                {layout === 'form' ? (
                  <div className="wrapper form-view">
                    <form
                      className="sign-in-form"
                      onSubmit={mode === 'password' ? handlePasswordSubmit : handleSubmit}
                    >
                      <div className="heading">
                        <Text className="title" text="Sign in" />
                        <Text className="subtitle" text="We’ll email you a magic link for a password-free sign in." />
                      </div>

                      {/* Order is platform-keyed (authModeOrder): the default mode lands first, so
                        web shows Magic link first, native shows Password first. The active pill
                        tracks `mode`. */}
                      <div className="mode-toggle" role="group">
                        {authModeOrder(nativeApp).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`mode-option${mode === option ? ' active' : ''}`}
                            onClick={() => handleModeChange(option)}
                          >
                            <Text text={option === 'password' ? 'Password' : 'Magic link'} />
                          </button>
                        ))}
                      </div>

                      {passwordError ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="Email or password is incorrect" />
                          </div>
                        </div>
                      ) : null}

                      {linkExpired ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="Your login link has expired" />
                            <Text
                              className="notice-hint"
                              text="Enter your email below to get a fresh sign-in link."
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="input">
                        <input
                          ref={emailRef}
                          type="email"
                          name="email"
                          autoComplete="email"
                          placeholder={t('Enter your email to begin')}
                          value={email ?? ''}
                          onChange={handleChange}
                          {...inputProps}
                        />
                      </div>

                      {mode === 'password' ? (
                        <>
                          <div className="input password-input">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              autoComplete="current-password"
                              placeholder={t('Enter your password')}
                              value={password}
                              onChange={handlePasswordChange}
                            />
                            <button
                              type="button"
                              className="eye-toggle"
                              aria-label={t(showPassword ? 'Hide password' : 'Show password')}
                              onClick={() => setShowPassword((v) => !v)}
                            >
                              <i
                                className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                                aria-hidden="true"
                              />
                            </button>
                          </div>

                          <Link className="forgot-link" to={ROUTE.resetPassword()}>
                            <Text text="Forgot password?" />
                          </Link>
                        </>
                      ) : null}

                      <div className="cta">
                        {mode === 'password' ? (
                          <button
                            type="submit"
                            className={`button${canSubmitPassword ? '' : ' disabled'}`}
                            disabled={!canSubmitPassword}
                          >
                            <Text className="text" text="Sign in" />
                            <i className="bi bi-box-arrow-in-right icon" aria-hidden="true" />
                          </button>
                        ) : (
                          <CtaHint email={email} disabled={!isValid}>
                            <button
                              type="submit"
                              className={`button${isValid ? '' : ' disabled'}`}
                              disabled={!isValid}
                              aria-describedby={isValid ? undefined : CTA_HINT_ID}
                            >
                              <Text className="text" text="Send magic link" />
                              <i className="bi bi-send-fill icon" aria-hidden="true" />
                            </button>
                          </CtaHint>
                        )}
                      </div>
                    </form>

                    <SocialLoginBar
                      redirectTargetRef={redirectTargetRef}
                      onResolved={() => setSsoResolved(true)}
                      onBusyChange={setLoading}
                    />

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Text className="label" text="Don’t have a brand yet?" />
                      <Link className="link" to="/register">
                        <Text text="Register." />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="wrapper sent-view">
                    <div className="heading">
                      <button
                        type="button"
                        className="back"
                        onClick={handleBackToForm}
                        aria-label={t('Back')}
                      >
                        <i className="bi bi-arrow-left" aria-hidden="true" />
                      </button>

                      <div className="heading-copy">
                        <Text className="title" text="An email is on the way" />
                        <span className="subtitle">
                          <Text text="We have sent you a secure login link to your email address." />{' '}
                          <Text text="It will log you in if you have a Whitelabels.com account." />
                        </span>
                      </div>
                    </div>

                    <div className="email-chip">
                      <i className="bi bi-envelope icon" aria-hidden="true" />
                      <Text className="email" text={email ?? ''} />
                    </div>

                    <div className="art">
                      <img className="mail-sent" src={MAIL_SENT_SRC} alt="" />
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <Text className="warning" text="The link expires in 15 minutes." />
                  </div>
                )}
              </div>
            </div>
          </div>

          <LanguagePicker languages={languages} currentLanguage={currentLanguage} onChange={onChangeLanguage} />
        </div>
      </div>
    </Login>
  );
};

export default PageLogin;
